import type { JSX } from "preact";
import type { RenderElementProps } from "slate-react";
import { Table } from "@gss/components/Table";
import { Image } from "./Image";
import { Link } from "./Link";

export function Element({
    element,
    attributes,
    children,
}: RenderElementProps): JSX.Element {
    switch (element.type) {
        case "image":
            return (
                <Image element={element} attributes={attributes}>
                    {children}
                </Image>
            );
        case "link":
            return (
                <Link element={element} attributes={attributes}>
                    {children}
                </Link>
            );
        case "table":
            return (
                <Table bordered bodyProps={attributes}>
                    {children}
                </Table>
            );
        case "table-row":
            return <tr {...attributes}>{children}</tr>;
        case "table-cell":
            return <td {...attributes}>{children}</td>;
        case "paragraph":
        case "block-quote":
        case "heading-one":
        case "heading-two":
        case "bulleted-list":
        case "numbered-list":
        case "list-item":
            break;
        // no default
    }

    const style = {
        textAlign: element.align,
        whiteSpace:
            element.align === "justify" ? ("normal" as const) : undefined,
    };

    switch (element.type) {
        case "paragraph":
            return (
                <p style={style} {...attributes}>
                    {children}
                </p>
            );
        case "block-quote":
            return (
                <blockquote style={style} {...attributes}>
                    {children}
                </blockquote>
            );
        case "heading-one":
            return (
                <h1 style={style} {...attributes}>
                    {children}
                </h1>
            );
        case "heading-two":
            return (
                <h2 style={style} {...attributes}>
                    {children}
                </h2>
            );
        case "bulleted-list":
            return (
                <ul style={style} {...attributes}>
                    {children}
                </ul>
            );
        case "numbered-list":
            return (
                <ol style={style} {...attributes}>
                    {children}
                </ol>
            );
        case "list-item":
            return (
                <li style={style} {...attributes}>
                    {children}
                </li>
            );
        default: {
            const _ensureExhaustive: never = element;
            throw new Error("Unknown element value");
        }
    }
}
